import * as L from 'leaflet'
import 'leaflet.markercluster'
import 'leaflet-polylinedecorator'

import BaseSchemaDrawer      from '../../../utils/map/BaseSchemaDrawer.js'
import { OBJECT }            from '../../../utils/map/types.js'
import { TREADS_POLY_STYLE } from '../../../config/map'

class ThreadsDrawer extends BaseSchemaDrawer {
  _relations   = []
  _arrowBodies = {}
  _arrowHeads  = {}
  
  bringToMarker(obj, zoomTo) {
    this.updateZoom()
    let zoom = zoomTo || obj.showAfterZoom || this._zoom
    if (zoom >= this._zoom) {
      zoom = zoom + 1
    }
    if (zoom < 8) {
      zoom = 9
    }
    let marker = this.getOrCreateMarker(obj)
    this._map.setView(marker.getLatLng(), zoom)
    this.drawIfNotExist(marker)
    this.createPopup(marker, { forceReDraw: true })
    return marker
  }
  
  initObservers() {
    this.$emitter = this.vm.$emitter
  }
  
  redrawAll() {
    this.removeAllPaths()
    this.drawArrows()
    this.drawCities()
    // this.drawTkoObjects()
  }
  
  removeAllPaths() {
    for (let key in this._arrowBodies) {
      if (this._arrowBodies[key]) {
        this._arrowBodies[key].remove()
        delete this._arrowBodies[key]
      }
    }
    for (let key in this._arrowHeads) {
      if (this._arrowHeads[key]) {
        this._arrowHeads[key].remove()
        delete this._arrowHeads[key]
      }
    }
    this._arrowBodies = {}
    this._arrowHeads  = {}
  }
  
  refreshMap(relations, settlements = []) {
    console.log(relations)
    this.removeAllPaths()
    this.removeAllObjects()
    this.setRelations(relations)
    this.setCities(settlements)
    
    this.$emitter.emit('geo.threadsDrawer.refreshed', {})
  }
  
  setRelations(relations) {
    this._relations = relations || []
    this.drawArrows()
  }
  
  drawArrows() {
    this.updateZoom()
    if (!this._relations?.length) {
      return
    }
    for (const v of this._relations) {
      this.getOrCreateArrow(v)
    }
  }
  
  drawTkoObjects() {
    this.updateZoom()
    this.createClusterIfNotExist()
    let markers = []
    this._tkoObjects.forEach(v => {
      markers.push(this.drawTkoObject(v))
    })
    this._tkoCluster.addLayer(markers)
  }
  
  drawTkoObject(v) {
    // console.log(v)
    const openedPopupInfo = this.vm.$route.query.openedPopupInfo
    const markerId        = this.getMarketId(v)
    if (this._tkoCluster.options.markersInLayer.indexOf(markerId) === -1) {
      const marker = this.getOrCreateMarker(v)
      marker.on('click', () => {
        this.createPopup(marker)
      })
      if (openedPopupInfo && marker.options.openedPopupInfo === openedPopupInfo) {
        this.createPopup(marker)
      }
    }
    return marker
  }
  
  getOrCreateArrow({ start, end, relationType }) {
    let model    = {
      type: OBJECT.REL,
      relationType,
      start,
      end
    }
    const lineId = this.getMarketId(model)
    
    const cantShowExistingStartAndEnd = (this.isCanShowOnThatZoom(start)) === false
    if (cantShowExistingStartAndEnd) {
      this.removeMarkerIfExist(start)
      return
    }
    
    if (!this._arrowBodies?.[lineId]) {
      this._arrowBodies[lineId] = this.drawArrow(model)
    }
    
    if (this.vm.$route.query.openedPopupInfo && lineId === this.vm.$route.query.openedPopupInfo) {
      if (this.vm.$route.query.lastEventPos) {
        let [lat, lng] = this.vm.$route.query.lastEventPos.split(',')
        this.createPopup(this._arrowBodies[lineId], { lastEventPos: `${lat},${lng}` }).setLatLng(L.latLng(lat, lng))
      } else {
        this.createPopup(this._arrowBodies[lineId])
      }
    }
  }
  
  defineArrowStyle(start, end) {
    let polyStyle = {}
    if (end.is_plan || start.is_plan) {
      polyStyle = { ...TREADS_POLY_STYLE.planned }
    } else {
      polyStyle = { ...TREADS_POLY_STYLE.fact }
    }
    polyStyle.weight = this._zoom * 0.2
    
    polyStyle.color = end.color ?? start.color
    
    return polyStyle
  }
  
  removePolyline(lineId) {
    if (this._arrowBodies[lineId]) {
      this._arrowBodies[lineId].remove()
      delete this._arrowBodies[lineId]
    }
    this.removeArrowHead(lineId)
  }
  
  removeArrowHead(lineId) {
    if (this._arrowHeads[lineId]) {
      this._arrowHeads[lineId].remove()
      delete this._arrowHeads[lineId]
    }
  }
  
  drawArrow(model) {
    let startMarker = this.getOrCreateMarker(model.start)
    let endMarker   = this.getOrCreateMarker(model.end)
    
    this.drawIfNotExist(startMarker)
    this.drawIfNotExist(endMarker)
    
    startMarker.bringToFront && startMarker.bringToFront()
    endMarker.bringToFront && endMarker.bringToFront()
    
    this.openTooltipByCriteria(startMarker, process.env.REGION === 'primor' && startMarker.options?.model?.type === OBJECT.TKO)
    this.openTooltipByCriteria(endMarker, (process.env.REGION === 'primor'))
    
    let arrowStyle = this.defineArrowStyle(model.start, model.end)
    
    const lineId        = this.getMarketId(model)
    let polyLineOptions = { ...arrowStyle, lineId, model, remove: (layer) => this.removePolyline(layer) }
    
    this._arrowBodies[lineId] = L.polyline([startMarker.getLatLng(), endMarker.getLatLng()], polyLineOptions)
    this._arrowBodies[lineId].bindTooltip(model.end.relationTooltip, { sticky: true }).addTo(this._map)
    
    // offset подобрано так,
    // чтобы при масштабе 4 не было видно стрелки.
    // Адекватное решение — это рисовать маркеры с нужной стрелкой
    this.drawArrowHead(this._arrowBodies[lineId])
    
    this.setClickHandlerForPolyline(this._arrowBodies[lineId])
    return this._arrowBodies[lineId]
  }
  
  setClickHandlerForPolyline(polyline) {
    let self = this
    polyline.on('click', function(e) {
      this.options.lastEventPos   = e.latlng
      this.options.lastEventPoint = e.layerPoint
      this.options.lastEvent      = e
      this.options.lastEventNoPan = true
      let { lat = 0, lng = 0 }    = e.latlng || {}
      self.createPopup(polyline, { forceReDraw: true, lastEventPos: `${lat},${lng}` })
    })
  }
  
  drawArrowHead(polyline) {
    const arrowHeadOptions = {
      pixelSize:   polyline.options.arrowSize,
      polygon:     false,
      pathOptions: { color: polyline.options.color, weight: polyline.options.weight, stroke: true }
    }
    
    const arrowOptions = {
      patterns: [{ offset: 18, repeat: 100, symbol: L.Symbol.arrowHead(arrowHeadOptions) }]
    }
    if (!this._arrowHeads?.[polyline.options.lineId]) {
      this._arrowHeads[polyline.options.lineId] = L.polylineDecorator(polyline, arrowOptions).addTo(this._map)
    }
  }
  
  openTooltipByCriteria(marker, criteria) {
    if (criteria) {
      marker.openTooltip()
    }
  }
}

export default ThreadsDrawer
